import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Button';
import { Input } from '@components/forms/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/Card';
import { AnonymousHeader } from '@components/layout/AnonymousHeader';
import { AnonymousFooter } from '@components/layout/AnonymousFooter';
import { authService } from './authService';

interface PasswordResetConfirmFormData {
  new_password1: string;
  new_password2: string;
}

const PasswordResetConfirm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<PasswordResetConfirmFormData>({
    new_password1: '',
    new_password2: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const uid = searchParams.get('uid');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!uid || !token) {
      setError(t('auth.invalidResetLink'));
    }
  }, [uid, token, t]);

  const handleInputChange = (field: keyof PasswordResetConfirmFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError('');
  };

  const validatePasswords = (): boolean => {
    if (formData.new_password1.length < 8) {
      setError(t('validation.passwordTooShort'));
      return false;
    }
    if (formData.new_password1 !== formData.new_password2) {
      setError(t('validation.passwordsDoNotMatch'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uid || !token) {
      setError(t('auth.invalidResetLinkShort'));
      return;
    }

    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      await authService.confirmPasswordReset({
        uid,
        token,
        new_password1: formData.new_password1,
        new_password2: formData.new_password2,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.passwordResetError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 via-secondary-500 to-primary-600 flex flex-col password-reset-confirm-page">
        {/* Anonymous Header */}
        <AnonymousHeader />

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-2xl">
            <CardHeader className="text-center pb-8">
              <div className="mx-auto mb-6 w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <span className="material-icons text-2xl text-white">check_circle</span>
              </div>
              <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('auth.passwordResetComplete')}
              </CardTitle>
              <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
                {t('auth.passwordResetCompleteMessage')}
              </CardDescription>
            </CardHeader>

            <CardContent className="px-8 pb-8">
              <div className="text-center space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t('auth.signInWithNewPassword')}
                </p>
              </div>

              <div className="mt-8 space-y-4">
                <Button
                  variant="gradient-multi"
                  size="lg"
                  className="w-full"
                  onClick={handleBackToLogin}
                >
                  {t('auth.signIn')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Anonymous Footer */}
        <AnonymousFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-secondary-500 to-primary-600 flex flex-col password-reset-confirm-page">
      {/* Anonymous Header */}
      <AnonymousHeader />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-8">
            <div className="mx-auto mb-6 w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
              <span className="material-icons text-2xl text-white">lock_reset</span>
            </div>
            <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('auth.setNewPassword')}
            </CardTitle>
            <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
              {t('auth.enterNewPasswordBelow')}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New Password Input */}
              <div className="space-y-2">
                <label
                  htmlFor="new_password1"
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  {t('auth.newPassword')} *
                </label>
                <div className="relative">
                  <Input
                    id="new_password1"
                    type={showPassword1 ? 'text' : 'password'}
                    value={formData.new_password1}
                    onChange={e => handleInputChange('new_password1', e.target.value)}
                    placeholder={t('auth.enterNewPassword')}
                    required
                    className="w-full pr-10"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword1(!showPassword1)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    disabled={isLoading}
                  >
                    <span className="material-icons text-lg">
                      {showPassword1 ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Confirm Password Input */}
              <div className="space-y-2">
                <label
                  htmlFor="new_password2"
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  {t('auth.confirmNewPassword')} *
                </label>
                <div className="relative">
                  <Input
                    id="new_password2"
                    type={showPassword2 ? 'text' : 'password'}
                    value={formData.new_password2}
                    onChange={e => handleInputChange('new_password2', e.target.value)}
                    placeholder={t('auth.confirmYourNewPassword')}
                    required
                    className="w-full pr-10"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword2(!showPassword2)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    disabled={isLoading}
                  >
                    <span className="material-icons text-lg">
                      {showPassword2 ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <p>{t('auth.passwordRequirementsList')}</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>{t('auth.atLeast8Characters')}</li>
                  <li>{t('auth.passwordsMustMatch')}</li>
                </ul>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Reset Password Button */}
              <Button
                type="submit"
                variant="gradient-multi"
                size="lg"
                className="w-full"
                disabled={isLoading || !uid || !token}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('auth.resettingPassword')}
                  </div>
                ) : (
                  t('auth.resetPassword')
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">
                  {t('common.or')}
                </span>
              </div>
            </div>

            {/* Back to Login Button */}
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              {t('auth.backToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Anonymous Footer */}
      <AnonymousFooter />
    </div>
  );
};

export { PasswordResetConfirm };
